/**
 * Phase 14 Plan 14-02 — ApproverHistoryPage
 *
 * Route: /logsheets/approver/history
 * Permission gate: logsheet.approver.read-history
 *
 * Paginated read-only history of the approver's own actions (Approved + Rejected entries).
 * Includes a CSV export button that downloads the streaming CSV blob.
 * Mirrors CheckerHistoryPage style (Phase 13 Plan 13-02).
 */

import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { downloadHistoryCsv, useApproverHistoryQuery } from '@/hooks/useLogsheetApproverQueries';
import type { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';
import { formatMachineName } from '@/lib/utils/machine-label';
import type { ApproverHistoryItem, ApproverHistoryListQuery } from '@/types/logsheet-approver';

const PAGE_SIZE = 20;

export function ApproverHistoryPage(): JSX.Element {
  const { permissions } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialFromDate = searchParams.get('fromDate') ?? '';
  const initialToDate = searchParams.get('toDate') ?? '';
  const initialStatus = (searchParams.get('status') ?? '') as '' | 'Approved' | 'Rejected';
  const initialPage = (() => {
    const p = Number.parseInt(searchParams.get('page') ?? '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  })();

  const [fromDate, setFromDate] = useState<string>(initialFromDate);
  const [toDate, setToDate] = useState<string>(initialToDate);
  const [statusFilter, setStatusFilter] = useState<'' | 'Approved' | 'Rejected'>(initialStatus);
  const [page, setPage] = useState<number>(initialPage);
  const [csvDownloading, setCsvDownloading] = useState<boolean>(false);

  useEffect(() => {
    const next = new URLSearchParams();
    if (fromDate !== '') next.set('fromDate', fromDate);
    if (toDate !== '') next.set('toDate', toDate);
    if (statusFilter !== '') next.set('status', statusFilter);
    if (page !== 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [fromDate, toDate, statusFilter, page, setSearchParams]);

  useEffect(() => {
    setPage(1);
  }, [fromDate, toDate, statusFilter]);

  const queryArgs: ApproverHistoryListQuery = useMemo(() => {
    const out: { -readonly [K in keyof ApproverHistoryListQuery]: ApproverHistoryListQuery[K] } = {
      page,
      pageSize: PAGE_SIZE,
    };
    if (fromDate !== '') out.fromDate = fromDate;
    if (toDate !== '') out.toDate = toDate;
    if (statusFilter !== '') out.status = statusFilter;
    return out;
  }, [fromDate, toDate, statusFilter, page]);

  const historyQuery = useApproverHistoryQuery(queryArgs);

  const columns = useMemo<ColumnDef<ApproverHistoryItem>[]>(
    () => [
      {
        accessorKey: 'entryDate',
        header: 'Date',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.entryDate}</span>,
      },
      {
        accessorKey: 'siteName',
        header: 'Site',
        cell: ({ row }) => <span>{row.original.siteName}</span>,
      },
      {
        accessorKey: 'machineName',
        header: 'Machine',
        cell: ({ row }) => (
          <span>
            {formatMachineName(
              row.original.machineName,
              row.original.vehicleNumber,
              row.original.machineCode,
            )}
          </span>
        ),
      },
      {
        accessorKey: 'makerName',
        header: 'Maker',
        cell: ({ row }) => <span>{row.original.makerName}</span>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge
            variant={
              row.original.status === 'Approved'
                ? 'default'
                : row.original.status === 'Rejected'
                  ? 'destructive'
                  : 'secondary'
            }
          >
            {row.original.status}
          </Badge>
        ),
      },
      {
        accessorKey: 'actedAt',
        header: 'Acted At',
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {row.original.actedAt !== null
              ? new Date(row.original.actedAt).toLocaleDateString()
              : '—'}
          </span>
        ),
      },
      {
        accessorKey: 'approverRejectionRoutedToChecker',
        header: 'Routed To Checker',
        cell: ({ row }) =>
          row.original.approverRejectionRoutedToChecker ? (
            <Badge variant="secondary">Routed to Checker</Badge>
          ) : null,
      },
    ],
    [],
  );

  const data = historyQuery.data?.items ?? [];
  const total = historyQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const table = useReactTable({
    data: data as ApproverHistoryItem[],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!permissions.includes('logsheet.approver.read-history')) {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleCsvExport(): Promise<void> {
    setCsvDownloading(true);
    try {
      await downloadHistoryCsv(queryArgs);
    } catch (err) {
      const apiErr = err as ApiError;
      toast.error('CSV export failed', { description: apiErr.detail ?? apiErr.message });
    } finally {
      setCsvDownloading(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="approver-history-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Approval History</h1>
          <p className="text-muted-foreground">Entries you have approved or rejected.</p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => void handleCsvExport()}
          disabled={csvDownloading}
          data-testid="approver-history-csv-export"
        >
          {csvDownloading ? 'Exporting…' : 'Export CSV'}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>History</CardTitle>
          <CardDescription>Filter by status and date range.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-44 space-y-1">
              <Label htmlFor="approver-history-filter-status">Status</Label>
              <Select
                value={statusFilter === '' ? 'all' : statusFilter}
                onValueChange={(v) => {
                  if (v === 'all') {
                    setStatusFilter('');
                  } else if (v === 'Approved' || v === 'Rejected') {
                    setStatusFilter(v);
                  }
                }}
              >
                <SelectTrigger
                  id="approver-history-filter-status"
                  data-testid="approver-history-filter-status"
                >
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="approver-history-filter-status-all">
                    All statuses
                  </SelectItem>
                  <SelectItem
                    value="Approved"
                    data-testid="approver-history-filter-status-Approved"
                  >
                    Approved
                  </SelectItem>
                  <SelectItem
                    value="Rejected"
                    data-testid="approver-history-filter-status-Rejected"
                  >
                    Rejected
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-40 space-y-1">
              <Label htmlFor="approver-history-filter-from-date">From date</Label>
              <Input
                id="approver-history-filter-from-date"
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                }}
                data-testid="approver-history-filter-from-date"
              />
            </div>
            <div className="w-40 space-y-1">
              <Label htmlFor="approver-history-filter-to-date">To date</Label>
              <Input
                id="approver-history-filter-to-date"
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                }}
                data-testid="approver-history-filter-to-date"
              />
            </div>
          </div>

          {historyQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="approver-history-loading">
              Loading history…
            </p>
          )}
          {historyQuery.isError && (
            <p className="text-sm text-destructive" data-testid="approver-history-error">
              Failed to load history.
            </p>
          )}

          {historyQuery.data && (
            <>
              <div data-testid="approver-history-table">
                {/* Mobile card list */}
                <div className="md:hidden space-y-3">
                  {table.getRowModel().rows.length === 0 ? (
                    <p className="text-sm text-center py-8 text-muted-foreground" data-testid="approver-history-empty">
                      No history entries.
                    </p>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <div
                        key={row.id}
                        className="rounded-lg border p-4 shadow-sm"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <div className="font-medium text-sm">{formatMachineName(row.original.machineName, row.original.vehicleNumber)}</div>
                            <div className="text-xs text-muted-foreground font-mono">{row.original.machineCode}</div>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <Badge variant={row.original.status === 'Approved' ? 'default' : row.original.status === 'Rejected' ? 'destructive' : 'secondary'}>
                              {row.original.status}
                            </Badge>
                            {row.original.approverRejectionRoutedToChecker && (
                              <Badge variant="secondary">Routed to Checker</Badge>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <div><span className="font-medium text-foreground">Date: </span><span className="font-mono">{row.original.entryDate}</span></div>
                          <div><span className="font-medium text-foreground">Site: </span>{row.original.siteName}</div>
                          <div><span className="font-medium text-foreground">Maker: </span>{row.original.makerName}</div>
                          <div><span className="font-medium text-foreground">Acted: </span><span className="font-mono">{row.original.actedAt !== null ? new Date(row.original.actedAt).toLocaleDateString() : '—'}</span></div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      {table.getHeaderGroups().map((hg) => (
                        <TableRow key={hg.id}>
                          {hg.headers.map((h) => (
                            <TableHead key={h.id}>
                              {h.isPlaceholder
                                ? null
                                : flexRender(h.column.columnDef.header, h.getContext())}
                            </TableHead>
                          ))}
                        </TableRow>
                      ))}
                    </TableHeader>
                    <TableBody>
                      {table.getRowModel().rows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={columns.length} className="text-center py-8">
                            <span data-testid="approver-history-empty">No history entries.</span>
                          </TableCell>
                        </TableRow>
                      ) : (
                        table.getRowModel().rows.map((row) => (
                          <TableRow
                            key={row.id}
                            data-testid={`approver-history-row-${String(row.original.id)}`}
                            className="hover:bg-muted/40"
                          >
                            {row.getVisibleCells().map((cell) => (
                              <TableCell key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-muted-foreground">
                  Page {page} of {pageCount} · {total} total
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                    }}
                    disabled={page <= 1}
                    data-testid="approver-history-pagination-prev"
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.min(pageCount, p + 1));
                    }}
                    disabled={page >= pageCount}
                    data-testid="approver-history-pagination-next"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
